/**
 * SectionNav — the one in-page tab row (design-grammar §4.5).
 *
 * A thin wrapper over the Radix `Tabs` in `components/ui/tabs.tsx`, so every
 * tab row on the platform inherits `role="tablist"`, `aria-selected` and
 * arrow-key movement from one implementation instead of three hand-rolled
 * `<button>` rows. `TRFerSectionNav`, `CommunitySectionNav` and
 * `MedicalSectionNav` render through it and keep their look; two of the three
 * had no `aria-selected` and none of the three had arrow keys.
 *
 * One variant, `underline` (#1927, PM ruling 2026-10-01): section
 * navigation, and the ONLY shape for it — a row that switches between
 * sections of one page (TRFer sections, Community spaces, Health, Ledger),
 * optionally numbered `01 / 02 / 03`. The `pill` variant was deleted with its
 * last caller (#2176): the copy editor's Edit / See it in place switch is a
 * view option over the same content, so it is a segmented control with
 * `aria-pressed` buttons and never carries `role="tab"` (§4.5).
 *
 * What it deliberately does NOT do:
 *   - It renders no panel. Every consumer today renders its own content —
 *     conditionally (Health, TRFer) or in a separate Radix `Tabs` (Community)
 *     — so the triggers carry no `aria-controls`; a dangling idref is worse
 *     than none. A page that does render an identified panel passes
 *     `controls` on the item and gets the association back.
 *   - It does not own the URL. `?tab=` sync stays on the page, which is where
 *     the fallback rules for an unavailable tab live (§4.5).
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MonoLabel } from './MonoLabel';

export type SectionNavVariant = 'underline';

export interface SectionNavItem<S extends string> {
  value: S;
  label: string;
  /** Plain total, rendered beside the label — never inside it (§4.5). */
  count?: number;
  /** Small danger dot; the count that earned it belongs in a StatusChip. */
  alert?: boolean;
  /** `id` of the panel this tab controls, when the page renders one. */
  controls?: string;
}

export interface SectionNavProps<S extends string> {
  value: S;
  onValueChange: (value: S) => void;
  items: SectionNavItem<S>[];
  variant?: SectionNavVariant;
  /** Prefix each tab with its `01`/`02` ordinal. */
  numbered?: boolean;
  /** `<nav>` landmark name. §4.8's fixed set names a tab row "Sections". */
  label?: string;
  className?: string;
}

// The Radix wrapper in `components/ui/tabs.tsx` ships the stock shadcn
// segmented-control look (muted pill track, rounded card on the active tab).
// The TRF underline is a different shape, so it restates the properties the
// stock look sets. `cn` inside the wrapper runs tailwind-merge with the TRF
// rungs registered (`lib/utils.ts`), so every pair below resolves last-wins
// rather than stacking two backgrounds.
const LIST_CLASS: Record<SectionNavVariant, string> = {
  underline:
    'flex h-auto items-end justify-start gap-6 rounded-none bg-transparent p-0 ' +
    'border-b border-border/60 -mb-px overflow-x-auto ' +
    '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
};

const TRIGGER_CLASS: Record<SectionNavVariant, string> = {
  underline:
    'group relative flex flex-shrink-0 items-baseline justify-start gap-2 ' +
    'rounded-none px-0 py-0 pb-3 -mb-px border-b-2 border-transparent ' +
    'text-stevens-ink-3 hover:text-foreground transition-colors ' +
    'data-[state=active]:bg-transparent data-[state=active]:shadow-none ' +
    'data-[state=active]:border-stevens-indigo data-[state=active]:text-foreground ' +
    'focus-visible:ring-stevens-indigo/40 focus-visible:ring-offset-0 focus-visible:rounded-sm',
};

const UNDERLINE_COUNT_CLASS = 'font-mono text-caption text-stevens-ink-3 tabular-nums';

export function SectionNav<S extends string>({
  value,
  onValueChange,
  items,
  variant = 'underline',
  numbered = false,
  label,
  className,
}: SectionNavProps<S>) {
  // §4.8 names a tab row “Sections”; the default follows the reader (#2109 R3 QA B-B).
  const { t } = useTranslation('common');
  // Radix activates a tab on `mousedown`, and `fireEvent.click` does not fire
  // one — so the trigger also handles `click`, and a real pointer click would
  // then emit twice. The mirror below is written during render, so it always
  // holds the value the parent last accepted: the second emit of a pair is a
  // no-op, and a parent that REJECTS the change (the value comes back
  // unchanged) still gets the next click, because the next render restores it.
  const acceptedRef = useRef<S>(value);
  acceptedRef.current = value;
  const emit = (next: string) => {
    if (next === acceptedRef.current) return;
    acceptedRef.current = next as S;
    onValueChange(next as S);
  };

  return (
    <Tabs asChild value={value} onValueChange={emit}>
      <nav aria-label={label ?? t('sectionNav.label')} className={className}>
        <TabsList className={LIST_CLASS[variant]}>
          {items.map((item, i) => (
            <TabsTrigger
              key={item.value}
              value={item.value}
              aria-controls={item.controls}
              onClick={() => emit(item.value)}
              className={TRIGGER_CLASS[variant]}
            >
              {numbered && (
                <MonoLabel className="text-stevens-ink-3/70">
                  {String(i + 1).padStart(2, '0')}
                </MonoLabel>
              )}
              <span className="font-serif relative text-card-title font-medium">
                {item.label}
                {item.alert && (
                  <span
                    aria-hidden
                    className="absolute -right-2 top-0 inline-block w-1.5 h-1.5 rounded-full bg-status-danger-fg"
                  />
                )}
              </span>
              {item.count != null && (
                <span className={UNDERLINE_COUNT_CLASS}>{item.count}</span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </nav>
    </Tabs>
  );
}
